import { Builder } from '@builder.io/react'
import CdnImage from 'components/util/CdnImage'
import { HeadingClass, HeadingType } from 'enums'
import React from 'react'
import CustomHeading, { headingTypePropObj, headingClassPropObj } from '../common/heading'

enum Alignment {
  Center = 'center',
  Left = 'left',
  Right = 'right'
}

interface Props {
  backgroundColor: string
  builderImageOne: any
  builderImageTwo: any
  builderImageThree: any
  imageOneAltText: string
  imageTwoAltText: string
  imageThreeAltText: string
  headingText: string
  headingTextColor: string
  contentText: string
  headingType: HeadingType
  headingClass: HeadingClass
  textAlignment: Alignment
  backgroundFooterImage: any
  backgroundFooterImageMobile: any
}

const TextThreeImage = ({
  backgroundColor,
  builderImageOne,
  builderImageTwo,
  builderImageThree,
  imageOneAltText,
  imageTwoAltText,
  imageThreeAltText,
  headingText,
  headingTextColor,
  contentText,
  headingType,
  headingClass,
  textAlignment,
  backgroundFooterImage,
  backgroundFooterImageMobile
}: Props) => {
  const getAlignmentClass = () => {
    if (textAlignment === Alignment.Right) {
      return 'text-right'
    }
    if (textAlignment === Alignment.Center) {
      return 'text-center'
    }
    return 'text-left'
  }

  const getContent = () => {
    return (
      <div className="mx-auto flex flex-col items-center justify-center mt-10">
        <CustomHeading
          classList={` ${getAlignmentClass()} line-height-fix w-full h-full tracking-tight text-gray-900 text-left font-medium`}
          headingClass={headingClass}
          headingType={headingType}
          color={headingTextColor}
        >
          {headingText}
        </CustomHeading>
        <div className="prose prose-indigo mt-5 text-gray-500 px-4 lg:px-0">
          <p
            className={`enable-list-styles text-base ${getAlignmentClass()}`}
            dangerouslySetInnerHTML={{ __html: contentText }}
          ></p>
        </div>
      </div>
    )
  }

  const getImages = () => {
    return (
      <div className="flex flex-row w-full justify-center mx-auto mb-8">
        {builderImageOne && (
          <CdnImage
            className="block rounded-2xl object-cover h-24 md:h-28 lg:h-32 w-auto mx-2 md:mx-4 lg:mx-10"
            url={builderImageOne}
            alt={imageOneAltText}
            steps={[168, 250, 320]}
          />
        )}
        {builderImageTwo && (
          <CdnImage
            className="block rounded-2xl object-cover h-24 md:h-28 lg:h-32 w-auto mx-2 md:mx-4 lg:mx-10"
            url={builderImageTwo}
            alt={imageTwoAltText}
            steps={[168, 250, 320]}
          />
        )}
        {builderImageThree && (
          <CdnImage
            className="block rounded-2xl object-cover h-24 md:h-28 lg:h-32 w-auto mx-2 md:mx-4 lg:mx-10"
            url={builderImageThree}
            alt={imageThreeAltText}
            steps={[168, 250, 320]}
          />
        )}
      </div>
    )
  }

  return (
    <>
      <div
        className="relative mx-auto flex w-full flex-col items-center justify-center lg:rounded-t-[30px] bg-white bg-auto bg-no-repeat lg:w-10/12 lg:max-w-[1024px]"
        style={{ backgroundColor: `${backgroundColor}` }}
      >
        {getContent()}
        {getImages()}
      </div>
      <div
        className={`relative mx-auto hidden w-10/12 bg-contain bg-top bg-no-repeat lg:block lg:max-w-[1024px] ${
          backgroundFooterImage ? 'h-28 2xl:h-56' : 'h-8'
        }`}
        style={{
          backgroundImage: backgroundFooterImage ? `url(${backgroundFooterImage})` : 'none'
        }}
      />
      <div
        className={`relative mx-auto lg:hidden w-full bg-cover bg-top bg-no-repeat block lg:container ${
          backgroundFooterImageMobile ? 'h-20 md:h-24' : 'h-8'
        }`}
        style={{
          backgroundImage: backgroundFooterImageMobile ? `url(${backgroundFooterImageMobile})` : 'none'
        }}
      />
    </>
  )
}

export default TextThreeImage

Builder.registerComponent(TextThreeImage, {
  name: 'TextThreeImage',
  inputs: [
    {
      name: 'backgroundColor',
      type: 'color',
      defaultValue: 'white'
    },

    {
      ...headingTypePropObj
    },
    {
      ...headingClassPropObj
    },
    {
      name: 'headingText',
      type: 'text',
      defaultValue: ''
    },
    {
      name: 'headingTextColor',
      type: 'color',
      defaultValue: 'black'
    },
    {
      name: 'contentText',
      type: 'richText',
      defaultValue: ''
    },
    {
      name: 'builderImageOne',
      type: 'file',
      allowedFileTypes: ['jpeg', 'png', 'svg']
    },
    {
      name: 'builderImageTwo',
      type: 'file',
      allowedFileTypes: ['jpeg', 'png', 'svg']
    },
    {
      name: 'builderImageThree',
      type: 'file',
      allowedFileTypes: ['jpeg', 'png', 'svg']
    },
    {
      name: 'imageOneAltText',
      type: 'text',
      defaultValue: ''
    },
    {
      name: 'imageTwoAltText',
      type: 'text',
      defaultValue: ''
    },
    {
      name: 'imageThreeAltText',
      type: 'text',
      defaultValue: ''
    },
    {
      name: 'textAlignment',
      type: 'string',
      enum: ['left', 'center', 'right'],
      defaultValue: 'left'
    },
    {
      name: 'backgroundFooterImage',
      type: 'file',
      defaultValue: null
    },
    {
      name: 'backgroundFooterImageMobile',
      type: 'file',
      defaultValue: null
    }
  ]
})
